/**
 * Auth Service - Irrigo Platform
 * Servicio de autenticación con JWT
 */

import { api, tokenManager, ApiResponse } from './api';
import type { LoginRequest, LoginResponse, RefreshTokenResponse, ApiUser } from '@/types/api';

// ============================================================================
// AUTH SERVICE
// ============================================================================

export const authService = {
  /**
   * Login con email y contraseña
   */
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await api.publicPost<LoginResponse>('/auth/login', credentials);
    
    // Si el login es exitoso, guardar el token
    if (response.success && response.data?.token) {
      tokenManager.setToken(response.data.token);
    }
    
    return response;
  },

  /**
   * Logout - limpiar tokens
   */
  async logout(): Promise<ApiResponse<{ message: string }>> {
    try {
      // Intentar logout en el servidor
      const response = await api.post<{ message: string }>('/auth/logout');
      return response;
    } finally {
      // Siempre limpiar tokens locales, incluso si el servidor falla
      tokenManager.clearAll();
    }
  },

  /**
   * Refrescar token
   */
  async refreshToken(): Promise<ApiResponse<RefreshTokenResponse>> {
    const response = await api.post<RefreshTokenResponse>('/auth/refresh');
    
    // Si el refresh es exitoso, actualizar el token
    if (response.success && response.data?.token) {
      tokenManager.setToken(response.data.token);
    }
    
    return response;
  },

  /**
   * Obtener usuario actual
   */
  async getCurrentUser(): Promise<ApiResponse<ApiUser>> {
    return api.get<ApiUser>('/auth/me');
  },

  /**
   * Verificar si hay sesión activa
   */
  isAuthenticated(): boolean {
    return tokenManager.isAuthenticated();
  },

  /**
   * Obtener token actual
   */
  getToken(): string | null {
    return tokenManager.getToken();
  },

  /**
   * Limpiar sesión local (sin llamar al servidor)
   */
  clearSession(): void {
    tokenManager.clearAll();
  },
};

export default authService;
